/**
 * Speak2-style role checks for Prntyr web.
 * Role order (low → high): Staff < Power User < Champion < Facility Admin < Corporate Admin.
 * System Admin is included above Corporate Admin for parity with Speak2 admin apps.
 *
 * @typedef {{ Name?: string, IsActive?: number | string | boolean }} Speak2Role
 * @typedef {{ Roles?: Speak2Role[] }} Speak2ProfileLike
 */

/** @type {Readonly<Record<string, string>>} */
const ROLE_NAMES = {
  STAFF: "Staff",
  POWER_USER: "Power User",
  CHAMPION: "Champion",
  FACILITY_ADMIN: "Facility Admin",
  CORPORATE_ADMIN: "Corporate Admin",
  SYSTEM_ADMIN: "System Admin",
};

/**
 * Numeric rank for each role (higher = more privilege).
 * Aligns with product: Media Library web admin is for Power User and above (rank >= 2).
 */
const ROLE_LEVEL = {
  [ROLE_NAMES.STAFF]: 1,
  [ROLE_NAMES.POWER_USER]: 2,
  [ROLE_NAMES.CHAMPION]: 3,
  [ROLE_NAMES.FACILITY_ADMIN]: 4,
  [ROLE_NAMES.CORPORATE_ADMIN]: 5,
  [ROLE_NAMES.SYSTEM_ADMIN]: 6,
};

const MEDIA_LIBRARY_MIN_LEVEL = ROLE_LEVEL[ROLE_NAMES.POWER_USER];
const CHAMPION_LEVEL = ROLE_LEVEL[ROLE_NAMES.CHAMPION];
const FACILITY_ADMIN_LEVEL = ROLE_LEVEL[ROLE_NAMES.FACILITY_ADMIN];

function isActiveRole(role) {
  if (!role || typeof role !== "object") return false;
  if (typeof role.IsActive === "string") {
    return role.IsActive.trim().toLowerCase() === "true" || Number(role.IsActive) === 1;
  }
  return role.IsActive === true || Number(role.IsActive) === 1;
}

/**
 * @param {Speak2ProfileLike | null | undefined} profile
 * @returns {string[]}
 */
function getActiveRoleNames(profile) {
  const roles = profile?.Roles;
  if (!Array.isArray(roles)) return [];
  return roles.filter(isActiveRole).map((r) => String(r?.Name || "").trim()).filter(Boolean);
}

/**
 * Highest role rank among the user's active Speak2 roles (0 if none match known roles).
 * @param {Speak2ProfileLike | null | undefined} profile
 * @returns {number}
 */
function getHighestSpeak2RoleLevel(profile) {
  const names = getActiveRoleNames(profile);
  let max = 0;
  for (const name of names) {
    const level = ROLE_LEVEL[name];
    if (typeof level === "number" && level > max) max = level;
  }
  return max;
}

/**
 * Media Library web admin: Power User and above (includes Champion, Facility Admin, Corporate Admin, System Admin).
 * Pass `response.data.Content` from `/users/profile/get` or any object with `Roles`.
 * @param {Speak2ProfileLike | null | undefined} profile
 * @returns {boolean}
 */
export function canAccessMediaLibrary(profile) {
  return getHighestSpeak2RoleLevel(profile) >= MEDIA_LIBRARY_MIN_LEVEL;
}

/**
 * Champion, Facility Admin, Corporate Admin, or System Admin (active roles only).
 * @param {Speak2ProfileLike | null | undefined} profile
 */
export function isChampionOrHigher(profile) {
  return getHighestSpeak2RoleLevel(profile) >= CHAMPION_LEVEL;
}

/**
 * Facility Admin, Corporate Admin, or System Admin (active roles only).
 * @param {Speak2ProfileLike | null | undefined} profile
 */
export function isFacilityAdminOrHigher(profile) {
  return getHighestSpeak2RoleLevel(profile) >= FACILITY_ADMIN_LEVEL;
}
